import clsx from "clsx";
import { ReactNode } from "react";

import {
  ComparableRubricField,
  getRubricLevelsByScore,
  rubricFieldDiffers,
} from "./outcome-rubric-comparison";

import { OutcomeImportRubricStrategy } from "api/api-server-outcome-transfer";
import { RubricLevelType, RubricType } from "components/server-types";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Checkbox } from "mds/components/Checkbox";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ScoreIcon } from "mds/icons";

type RubricOrigin = "destination" | "source";

function RubricDifferenceValue({
  children,
  changed,
  field,
  rubricOrigin,
}: {
  children: ReactNode;
  changed: boolean;
  field: ComparableRubricField;
  rubricOrigin: RubricOrigin;
}) {
  const isSourceRubric = rubricOrigin === "source";
  const fieldLabel = {
    name: t("outcome_transfer.level_name"),
    conversion_percent: t("home.settings.rubric_conversion_scale.title"),
    description: t("outcome_transfer.level_description"),
  }[field];

  return (
    <div
      className={clsx(
        "flex gap-1 rounded px-1 py-0.5",
        changed &&
          (isSourceRubric
            ? "border-l-2 border-green-tint-70 bg-green-tint-93"
            : "border-l-2 border-red-tint-70 bg-red-tint-95"),
      )}
    >
      {changed && (
        <>
          <span
            className={clsx(
              "w-3 shrink-0 font-semibold",
              isSourceRubric ? "text-green-shade-20" : "text-red-shade-20",
            )}
            aria-hidden
          >
            {isSourceRubric ? "+" : "−"}
          </span>
          <span className="sr-only">
            {t(
              isSourceRubric
                ? "outcome_transfer.source_difference"
                : "outcome_transfer.destination_difference",
              { field: fieldLabel },
            )}
          </span>
        </>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function RubricComparisonRow({
  comparisonRubricLevelsByScore,
  rubricLevel,
  rubricOrigin,
}: {
  comparisonRubricLevelsByScore: Map<number, RubricLevelType>;
  rubricLevel: RubricLevelType;
  rubricOrigin: RubricOrigin;
}) {
  return (
    <tr>
      <th className="border-b border-black-tint-90 py-2 pr-3 align-top font-normal" scope="row">
        <ScoreIcon value={rubricLevel.score} />
      </th>
      <td className="space-y-1 break-words border-b border-black-tint-90 py-2">
        <RubricDifferenceValue
          changed={rubricFieldDiffers(rubricLevel, comparisonRubricLevelsByScore, "name")}
          field="name"
          rubricOrigin={rubricOrigin}
        >
          <p className="font-medium">{rubricLevel.name}</p>
        </RubricDifferenceValue>
        <RubricDifferenceValue
          changed={rubricFieldDiffers(
            rubricLevel,
            comparisonRubricLevelsByScore,
            "conversion_percent",
          )}
          field="conversion_percent"
          rubricOrigin={rubricOrigin}
        >
          <p>
            {t("home.settings.rubric_conversion_scale.title")}: {rubricLevel.conversion_percent}%
          </p>
        </RubricDifferenceValue>
        <RubricDifferenceValue
          changed={rubricFieldDiffers(rubricLevel, comparisonRubricLevelsByScore, "description")}
          field="description"
          rubricOrigin={rubricOrigin}
        >
          <p className="py-1 font-medium">{t("outcome_transfer.level_description")}</p>
          <p>{rubricLevel.description}</p>
        </RubricDifferenceValue>
      </td>
    </tr>
  );
}

export function OutcomeRubricStrategyCard({
  canReplaceOrganizationRubric,
  comparisonRubric,
  destinationOutcomeCount,
  hasAcknowledgedRubricReplacement,
  isImportPending,
  organizationName,
  rubric,
  rubricOrigin,
  onAcknowledgedRubricReplacementChange,
  onImportWithRubricStrategy,
}: {
  canReplaceOrganizationRubric: boolean;
  comparisonRubric: RubricType;
  destinationOutcomeCount: number;
  hasAcknowledgedRubricReplacement: boolean;
  isImportPending: boolean;
  organizationName: string;
  rubric: RubricType;
  rubricOrigin: RubricOrigin;
  onAcknowledgedRubricReplacementChange: (hasAcknowledged: boolean) => void;
  onImportWithRubricStrategy: (strategy: OutcomeImportRubricStrategy) => void;
}) {
  const isSourceRubric = rubricOrigin === "source";
  const importStrategy: OutcomeImportRubricStrategy = isSourceRubric
    ? "replace_with_source"
    : "keep_destination";
  const comparisonRubricLevelsByScore = getRubricLevelsByScore(comparisonRubric);

  return (
    <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-black-tint-90 p-4">
      <h4 className="font-medium">
        {t(
          isSourceRubric ? "outcome_transfer.source_rubric" : "outcome_transfer.destination_rubric",
        )}
      </h4>
      <p className="break-words text-sm">{organizationName}</p>
      <table className="w-full text-left text-sm">
        <caption className="sr-only">
          {t(
            isSourceRubric
              ? "outcome_transfer.source_rubric"
              : "outcome_transfer.destination_rubric",
          )}
          : {organizationName}
        </caption>
        <thead>
          <tr>
            <th scope="col">{t("outcome_transfer.score")}</th>
            <th scope="col">{t("outcome_transfer.rubric_details")}</th>
          </tr>
        </thead>
        <tbody>
          {rubric.map((rubricLevel) => (
            <RubricComparisonRow
              comparisonRubricLevelsByScore={comparisonRubricLevelsByScore}
              key={rubricLevel.score}
              rubricLevel={rubricLevel}
              rubricOrigin={rubricOrigin}
            />
          ))}
        </tbody>
      </table>
      <Banner kind="warning">
        {t(
          isSourceRubric ? "outcome_transfer.source_impact" : "outcome_transfer.destination_impact",
          { count: destinationOutcomeCount },
        )}
      </Banner>
      {isSourceRubric &&
        (canReplaceOrganizationRubric ? (
          <Checkbox
            checked={hasAcknowledgedRubricReplacement}
            className="text-sm"
            disabled={isImportPending}
            onChange={(event) => onAcknowledgedRubricReplacementChange(event.target.checked)}
          >
            <span>{t("outcome_transfer.acknowledge_replacement")}</span>
          </Checkbox>
        ) : (
          <p className="text-sm" role="note">
            {t("outcome_transfer.assessments_block_replacement")}
          </p>
        ))}
      <SquareButton
        className="mt-auto h-auto min-h-11 whitespace-normal"
        disabled={
          isImportPending ||
          (isSourceRubric && (!canReplaceOrganizationRubric || !hasAcknowledgedRubricReplacement))
        }
        kind={isSourceRubric ? "caution" : "accent"}
        size="m"
        onClick={() => onImportWithRubricStrategy(importStrategy)}
      >
        {t(
          isSourceRubric
            ? "outcome_transfer.replace_and_import"
            : "outcome_transfer.destination_and_import",
        )}
      </SquareButton>
    </section>
  );
}
